import dayjs from "dayjs";
import i18n from "@/i18n/index";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

(jsPDF as any).API.autoTable = autoTable;

import { ref } from "vue";
import { currentClub } from "@/shared/state";
import { useGlobal } from "@/composables/global/useGlobal";
import { getInvoiceConfig } from "@/config/invoiceRegistry";

export function useInvoice() {
  const { formatDate, formatDaysToPeriod, translateToFrench } = useGlobal();
  const error = ref<string | null>(null);

  const invoicePDF = async (
    member_info: any,
    active_subscription: any,
    { logoImageData, logoNaturalWidth, logoNaturalHeight }: any,
    { signatureImageData, signatureNaturalWidth, signatureNaturalHeight }: any,
    typeOfFile: string,
  ) => {
    const doc = new jsPDF();
    const invoiceConfig = getInvoiceConfig(currentClub?.value ?? null);

    const memberInfo = {
      contractNumber: member_info?.contractNumber?.toString() ?? "",
      name: member_info?.name?.toString() ?? "",
      tele: member_info?.tele?.toString() ?? "",
      email: member_info?.email?.toString() ?? "",
      adresse: member_info?.adresse?.toString() ?? "",
      cin: member_info?.cin?.toString() ?? "",
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 10;
    const leftX = 10;
    const lineHeight = 6;

    const desiredWidth = 26;
    const aspectRatio =
      logoNaturalWidth && logoNaturalHeight
        ? logoNaturalHeight / logoNaturalWidth
        : 1;
    const desiredHeight = desiredWidth * aspectRatio;

    if (logoImageData && logoNaturalWidth && logoNaturalHeight) {
      doc.addImage(
        logoImageData,
        "JPEG",
        leftX,
        y - 5,
        desiredWidth,
        desiredHeight,
      );
      y += desiredHeight + 5;
    } else {
      y += 5;
    }

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(12);
    doc.text(currentClub?.value?.name ?? "", leftX, y);
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(8);
    const club = (currentClub?.value as any) ?? (null as any);
    const clubAdresse = club?.adresse ?? "";
    ("");
    const clubCity = club?.city ?? "";
    ("");
    const clubCountry = club?.country ?? "";
    ("");

    const clubTele = club?.tele ?? "";
    ("");
    const clubEmail = club?.email ?? "";
    ("");
    const clubIce = club?.ice ?? "";
    ("");
    if (clubAdresse) {
      doc.text(clubAdresse + ", " + clubCity + ", " + clubCountry, leftX, y);
      y += 4;
    }
    if (clubTele) {
      doc.text(clubTele, leftX, y);
      y += 4;
    }
    if (clubEmail) {
      doc.text(clubEmail, leftX, y);
      y += 4;
    }
    if (clubIce) {
      doc.text(clubIce, leftX, y);
      y += 4;
    }

    y += 4;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(16);
    doc.text(
      invoiceConfig.title ||
        translateToFrench("views.pdf.invoice.title") ||
        "FACTURE",
      pageWidth / 2,
      y,
      {
        align: "center",
      },
    );
    y += 8;

    const invoiceNumber =
      "FAC-" +
      (active_subscription?.id ?? 0) +
      "-" +
      dayjs().format("YYYYMMDD");
    const invoiceDate = formatDate(dayjs().toISOString());

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("views.pdf.invoice.number") || "N° Facture") +
        " : " +
        invoiceNumber,
      pageWidth - leftX,
      y,
      { align: "right" },
    );
    y += 5;
    doc.text(
      (translateToFrench("views.pdf.invoice.date") || "Date") +
        " : " +
        invoiceDate,
      pageWidth - leftX,
      y,
      { align: "right" },
    );
    y += 10;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.invoice.client_section") || "CLIENT",
      leftX,
      y,
    );
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("tables.members.name") || "Nom") +
        " : " +
        memberInfo.name,
      leftX,
      y,
    );
    y += 5;
    doc.text(
      (translateToFrench("tables.members.tele") || "Téléphone") +
        " : " +
        memberInfo.tele,
      leftX,
      y,
    );
    y += 5;
    doc.text(
      (translateToFrench("tables.members.email") || "Email") +
        " : " +
        memberInfo.email,
      leftX,
      y,
    );
    y += 5;
    if (memberInfo.adresse) {
      doc.text(
        (translateToFrench("tables.members.adresse") || "Adresse") +
          " : " +
          memberInfo.adresse,
        leftX,
        y,
      );
      y += 5;
    }
    y += 6;

    const invoiceDetailsHeaders = [
      translateToFrench("views.pdf.invoice.subscription_name") || "Abonnement",
      translateToFrench("views.pdf.invoice.description") || "Description",
      translateToFrench("views.pdf.invoice.quantity") || "Qté",
      translateToFrench("views.pdf.invoice.unit_price") || "Prix unitaire",
      translateToFrench("views.pdf.invoice.total_price") || "Total",
    ];

    const packPrice = active_subscription?.pack_price ?? 0;
    const subscriptionName = active_subscription?.subscription?.name ?? "";
    const packName = active_subscription?.pack?.name ?? "";
    const nbDays =
      active_subscription?.subscription?.nbDays ??
      (active_subscription?.subscription as any)?.club_subscription?.nbDays;
    const description = packName + (nbDays ? " - " + nbDays : "");
    const quantity = 1;
    const totalAmount =
      active_subscription?.total_amount ??
      active_subscription?.discounted_price ??
      packPrice;

    const invoiceDetailsData = [
      [
        subscriptionName || packName || "-",
        description,
        quantity.toString(),
        packPrice.toFixed(2) + " " + translateToFrench("symbole.currency.mad"),
        totalAmount.toFixed(2) +
          " " +
          translateToFrench("symbole.currency.mad"),
      ],
    ];

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.invoice.details_section") ||
        "DÉTAILS DE LA FACTURE",
      leftX,
      y,
    );
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [invoiceDetailsHeaders],
      body: invoiceDetailsData,
      //  theme: "grid",
      headStyles: { fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: leftX, right: leftX },
    });

    y = (doc as any).lastAutoTable?.finalY ?? y;
    y += 10;

    const paymentsHeaders = [
      translateToFrench("tables.payment.paymentDate") || "Date",
      translateToFrench("tables.payment.paymentMethod") || "Mode de paiement",
      translateToFrench("tables.payment.amount_paid") || "Montant payé",
    ];

    const paymentsData: Array<string[]> = [];
    if (active_subscription?.transactions?.length) {
      active_subscription.transactions.forEach((t: any) => {
        paymentsData.push([
          formatDate(t?.paymentDate) ?? "-",
          t?.paymentMethod ?? "-",
          (t?.amount_paid ?? 0).toFixed(2) +
            " " +
            translateToFrench("symbole.currency.mad"),
        ]);
      });
    }

    if (paymentsData.length > 0) {
      doc.setFont("helvetica", "normal", "bold");
      doc.setFontSize(10);
      doc.text(
        translateToFrench("views.pdf.invoice.payments_section") || "PAIEMENTS",
        leftX,
        y,
      );
      y += 6;

      autoTable(doc, {
        startY: y,
        head: [paymentsHeaders],
        body: paymentsData,
        //theme: "grid",
        headStyles: { fontSize: 8, fontStyle: "bold" },
        bodyStyles: { fontSize: 8 },
        margin: { left: leftX, right: leftX },
      });

      y = (doc as any).lastAutoTable?.finalY ?? y;
      y += 10;
    }

    const totalAmountVal = active_subscription?.total_amount ?? 0;
    const totalPaid = active_subscription?.total_amount_paid ?? 0;
    const remainingAmount = active_subscription?.remaining_amount ?? 0;

    const summaryX = pageWidth - 60;
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.invoice.summary_section") || "RÉSUMÉ",
      leftX,
      y,
    );
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("tables.regulations.net_total") || "Total") + " :",
      leftX,
      y,
    );
    doc.text(
      totalAmountVal.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      summaryX,
      y,
      { align: "right" },
    );
    y += 6;

    doc.text(
      (translateToFrench("tables.regulations.total_paid") || "Total payé") +
        " :",
      leftX,
      y,
    );
    doc.text(
      totalPaid.toFixed(2) + " " + translateToFrench("symbole.currency.mad"),
      summaryX,
      y,
      { align: "right" },
    );
    y += 6;

    doc.setFont("helvetica", "normal", "bold");
    doc.text(
      (translateToFrench("tables.regulations.remaining_amount") ||
        "Reste à payer") + " :",
      leftX,
      y,
    );
    doc.text(
      remainingAmount.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      summaryX,
      y,
      { align: "right" },
    );

    y += 10;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(8);
    doc.text(
      invoiceConfig.signatureLabel ||
        translateToFrench("views.pdf.invoice.signature") ||
        "Signature",
      leftX + 40,
      y,
    );
    doc.text(
      invoiceConfig.forClubLabel ||
        translateToFrench("views.pdf.invoice.for_club") ||
        "Pour le club",
      pageWidth - (leftX + 40),
      y,
      { align: "right" },
    );
    // Set the desired width and calculate height to maintain the aspect ratio
    const signatureDesiredWidth = 26;
    const signatureAspectRatio = signatureNaturalWidth / signatureNaturalWidth; // Height / Width to maintain aspect ratio
    const signatureDesiredHeight = signatureDesiredWidth * signatureAspectRatio;
    console.log("signatureImageData");
    console.log(signatureImageData);
    if (signatureImageData) {
      doc.addImage(
        signatureImageData,
        "JPEG",
        pageWidth - (leftX + 60),
        y + 2,
        signatureDesiredWidth,
        signatureDesiredHeight - 10,
      );
    }
    y += 12;

    const filename =
      "Facture_" +
      memberInfo.name.replace(/\s+/g, "_") +
      "_" +
      invoiceNumber +
      ".pdf";

    if (typeOfFile === "print") {
      doc.autoPrint();
      window.open(doc.output("bloburl"));
    } else {
      doc.save(filename);
    }
  };

  const devisPDF = async (
    member_info: any,
    active_subscription: any,
    { logoImageData, logoNaturalWidth, logoNaturalHeight }: any,
    { signatureImageData, signatureNaturalWidth, signatureNaturalHeight }: any,
    typeOfFile: string,
  ) => {
    const doc = new jsPDF();
    const invoiceConfig = getInvoiceConfig(currentClub?.value ?? null);

    const memberInfo = {
      contractNumber: member_info?.contractNumber?.toString() ?? "",
      name: member_info?.name?.toString() ?? "",
      tele: member_info?.tele?.toString() ?? "",
      email: member_info?.email?.toString() ?? "",
      adresse: member_info?.adresse?.toString() ?? "",
      cin: member_info?.cin?.toString() ?? "",
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 10;
    const leftX = 10;
    const lineHeight = 6;

    const desiredWidth = 26;
    const aspectRatio =
      logoNaturalWidth && logoNaturalHeight
        ? logoNaturalHeight / logoNaturalWidth
        : 1;
    const desiredHeight = desiredWidth * aspectRatio;

    if (logoImageData && logoNaturalWidth && logoNaturalHeight) {
      doc.addImage(
        logoImageData,
        "JPEG",
        leftX,
        y - 5,
        desiredWidth,
        desiredHeight,
      );
      y += desiredHeight + 5;
    } else {
      y += 5;
    }

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(12);
    doc.text(currentClub?.value?.name ?? "", leftX, y);
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(8);

    const clubAdresse = currentClub?.value?.address ?? "";
    const clubCity = currentClub?.value?.city ?? "";
    const clubCountry = currentClub?.value?.country ?? "";

    if (clubAdresse) {
      doc.text(clubAdresse + ", " + clubCity + ", " + clubCountry, leftX, y);
      y += 4;
    }
    if (currentClub?.value?.phone) {
      doc.text(currentClub?.value?.phone, leftX, y);
      y += 4;
    }
    if (currentClub?.value?.ice) {
      doc.text(currentClub?.value?.ice, leftX, y);
      y += 4;
    }
    y += 4;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(16);
    doc.text(
      translateToFrench("views.pdf.devis.title") || "DEVIS",
      pageWidth / 2,
      y,
      {
        align: "center",
      },
    );
    y += 8;

    const invoiceNumber =
      "DEVIS-" +
      (active_subscription?.id ?? 0) +
      "-" +
      dayjs().format("YYYYMMDD");
    const invoiceDate = formatDate(dayjs().toISOString());

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("views.pdf.devis.number") || "N° Devis") +
        " : " +
        invoiceNumber,
      pageWidth - leftX,
      y,
      { align: "right" },
    );
    y += 5;
    doc.text(
      (translateToFrench("views.pdf.devis.date") || "Date") +
        " : " +
        invoiceDate,
      pageWidth - leftX,
      y,
      { align: "right" },
    );
    y += 10;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.devis.client_section") || "CLIENT",
      leftX,
      y,
    );
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("tables.members.name") || "Nom") +
        " : " +
        memberInfo.name,
      leftX,
      y,
    );
    y += 5;
    doc.text(
      (translateToFrench("tables.members.tele") || "Téléphone") +
        " : " +
        memberInfo.tele,
      leftX,
      y,
    );
    y += 5;
    doc.text(
      (translateToFrench("tables.members.email") || "Email") +
        " : " +
        memberInfo.email,
      leftX,
      y,
    );
    y += 5;
    if (memberInfo.adresse) {
      doc.text(
        (translateToFrench("tables.members.adresse") || "Adresse") +
          " : " +
          memberInfo.adresse,
        leftX,
        y,
      );
      y += 5;
    }
    y += 6;

    const invoiceDetailsHeaders = [
      // translateToFrench("views.pdf.devis.subscription_name") || "Abonnement",
      // translateToFrench("views.pdf.devis.description") || "Description",
      // translateToFrench("views.pdf.devis.quantity") || "Qté",
      // translateToFrench("views.pdf.devis.unit_price") || "Prix unitaire",
      // translateToFrench("views.pdf.devis.unit_price") || "Prix unitaire",
      // translateToFrench("views.pdf.devis.unit_price") || "Prix unitaire",
      // translateToFrench("views.pdf.devis.total_price") || "Total",

      "Durée",
      "Pack",
      "Prix du pack",
      "Remise et promo",
      "Total Services",
      "Montant TTC",
    ];

    const packPrice =  active_subscription?.pack_price ?? 0;
    const subscriptionName = active_subscription?.subscription?.name ?? "";
    const packName = active_subscription?.pack?.name ?? "";
    const nbDays =
      active_subscription?.subscription?.nbDays ??
      (active_subscription?.subscription as any)?.club_subscription?.nbDays;
    const description = packName + (nbDays ? " - " + nbDays : "");
    const quantity = 1;
    const totalAmount =
      active_subscription?.total_amount ??
      active_subscription?.discounted_price ??
      packPrice;

    const {
      promotion,
      pack,
      promo_price: base_promo_price,
      pack_price: base_pack_price,
      total_amount: totalAmountRaw,
      total_fees: totalFees = 0,
      discount_amount = 0,
      total_services_price = 0,
      start_date,
      end_date,
      subscription,
    } = active_subscription;

    let totalAssurance = 0;
    if (active_subscription?.assurance) {
      totalAssurance = active_subscription?.assurance?.total_amount;
    }

    // --- Prices ---
    const promo_price = base_promo_price ?? 0;
    const origin_pack_price = base_pack_price ?? 0;
    const promoted_price = promo_price ? origin_pack_price - promo_price : 0;

    // Real total amount after removing fees & assurance
    const total_amount = (totalAmountRaw ?? 0) - (totalFees + totalAssurance);

    // Discount includes real discount + promotion difference
    const total_discount = discount_amount + promoted_price;

    const invoiceDetailsData = [
      [
        // subscriptionName || packName || "-",
        // description,
        // quantity.toString(),

        active_subscription.subscription?.name ?? "",
        active_subscription.pack?.name?.toString() ?? "",

        `${origin_pack_price?.toFixed(2)} DH`,
        `${total_discount?.toFixed(2)} DH`,
        `${total_services_price ?? 0} DH`,
        `${total_amount?.toFixed(2)} DH`,

        // packPrice.toFixed(2) + " " + translateToFrench("symbole.currency.mad"),
        // totalAmount.toFixed(2) +
        //   " " +
        //   translateToFrench("symbole.currency.mad"),
      ],
    ];

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.devis.details_section") ||
        "DÉTAILS DE LA DEVIS",
      leftX,
      y,
    );
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [invoiceDetailsHeaders],
      body: invoiceDetailsData,
      //  theme: "grid",
      headStyles: { fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: leftX, right: leftX },
    });

    y = (doc as any).lastAutoTable?.finalY ?? y;
    y += 10;

    const totalAmountVal = active_subscription?.total_amount ?? 0;

    const remainingAmount = active_subscription?.remaining_amount ?? 0;

    const summaryX = pageWidth - 60;
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(
      translateToFrench("views.pdf.devis.summary_section") || "RÉSUMÉ",
      leftX,
      y,
    );
    y += 6;

    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(9);
    doc.text(
      (translateToFrench("tables.regulations.net_total") || "Total") + " :",
      leftX,
      y,
    );
    doc.text(
      totalAmountVal.toFixed(2) +
        " " +
        translateToFrench("symbole.currency.mad"),
      summaryX,
      y,
      { align: "right" },
    );

    y += 10;

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(8);
    doc.text(
      invoiceConfig.signatureLabel ||
        translateToFrench("views.pdf.devis.signature") ||
        "Signature",
      leftX + 40,
      y,
    );
    doc.text(
      invoiceConfig.forClubLabel ||
        translateToFrench("views.pdf.devis.for_club") ||
        "Pour le club",
      pageWidth - (leftX + 40),
      y,
      { align: "right" },
    );
    // Set the desired width and calculate height to maintain the aspect ratio
    const signatureDesiredWidth = 26;
    const signatureAspectRatio = signatureNaturalWidth / signatureNaturalWidth; // Height / Width to maintain aspect ratio
    const signatureDesiredHeight = signatureDesiredWidth * signatureAspectRatio;
    console.log("signatureImageData");
    console.log(signatureImageData);
    if (signatureImageData) {
      doc.addImage(
        signatureImageData,
        "JPEG",
        pageWidth - (leftX + 60),
        y + 2,
        signatureDesiredWidth,
        signatureDesiredHeight - 10,
      );
    }
    y += 12;

    const filename =
      "Devis_" +
      memberInfo.name.replace(/\s+/g, "_") +
      "_" +
      invoiceNumber +
      ".pdf";

    if (typeOfFile === "print") {
      doc.autoPrint();
      window.open(doc.output("bloburl"));
    } else {
      doc.save(filename);
    }
  };

  return {
    invoicePDF,
    devisPDF,
    error,
  };
}
